/**
 * BP-RC1 Phase 1 — domainRouteConfig → sidebar/menu navigation items.
 * @see doc/architecture/BP-RC1-ROUTE-CONFIG-CONTRACT.md §3
 */

import type { AppMenuItem } from '../config/appMenuConfig';
import {
  buildAbsolutePath,
  expandDomainRouteEntries,
  resolveRouteId,
  resolveRouteLabel,
  type DomainRouteConfigEntry,
  type IconKey,
  type NormalizedDomainRouteEntry,
} from '../config/domainRouteConfigSchema';

export interface GeneratedNavigationItem {
  key: string;
  path: string;
  label: string;
  icon: IconKey | null;
  menuVisible: boolean;
  pageType: NormalizedDomainRouteEntry['pageType'];
  permissions: NormalizedDomainRouteEntry['permissions'];
  children?: GeneratedNavigationItem[];
}

export interface GenerateNavigationOptions {
  domainTop: string;
  routes: readonly DomainRouteConfigEntry[];
  domainKey?: string;
  domainLabel?: string;
  domainIcon?: IconKey | null;
  includeDashboard?: boolean;
}

export function buildDomainNavigationItems(
  options: GenerateNavigationOptions
): GeneratedNavigationItem[] {
  const {
    domainTop,
    routes,
    domainKey = domainTop,
    domainLabel,
    domainIcon = null,
    includeDashboard = true,
  } = options;

  const normalized = expandDomainRouteEntries(domainTop, routes, {
    includeDashboard,
    includeDetailRoutes: false,
    includeFieldsAndLayout: false,
  }).filter((entry) => entry.menuVisible);

  const children: GeneratedNavigationItem[] = normalized.map((entry) => ({
    key: entry.id,
    path: entry.absolutePath,
    label: entry.label,
    icon: entry.icon,
    menuVisible: entry.menuVisible,
    pageType: entry.pageType,
    permissions: entry.permissions,
  }));

  if (children.length === 0) {
    return [];
  }

  const rootPath =
    children.find((item) => item.pageType === 'overview')?.path ??
    children[0]?.path ??
    buildAbsolutePath(domainTop, 'dashboard');

  return [
    {
      key: domainKey,
      path: rootPath,
      label: domainLabel ?? domainTop,
      icon: domainIcon,
      menuVisible: true,
      pageType: 'overview',
      permissions: children[0]?.permissions ?? [`${routes[0]?.businessDomain ?? domainTop}:read`],
      children,
    },
  ];
}

export type IconNodeResolver = (icon: IconKey | null) => AppMenuItem['icon'];

export function toAppMenuItems(
  items: GeneratedNavigationItem[],
  resolveIcon: IconNodeResolver = () => null
): AppMenuItem[] {
  return items.map((item) => ({
    key: item.key,
    path: item.path,
    icon: resolveIcon(item.icon),
    label: item.label,
    ...(item.children?.length ? { children: toAppMenuItems(item.children, resolveIcon) } : {}),
  }));
}

export function flattenNavigationPaths(items: readonly GeneratedNavigationItem[]): string[] {
  const paths: string[] = [];

  function walk(nodes: readonly GeneratedNavigationItem[]): void {
    for (const node of nodes) {
      if (node.path) {
        paths.push(node.path);
      }
      if (node.children?.length) {
        walk(node.children);
      }
    }
  }

  walk(items);
  return paths;
}

export function buildLeafNavigationItems(
  domainTop: string,
  routes: readonly DomainRouteConfigEntry[]
): GeneratedNavigationItem[] {
  return routes
    .filter((route) => route.menuVisible !== false && !route.legacy)
    .map((route) => ({
      key: resolveRouteId(domainTop, route),
      path: buildAbsolutePath(domainTop, route.path),
      label: resolveRouteLabel(route),
      icon: route.icon ?? null,
      menuVisible: true,
      pageType: route.pageType ?? 'collection',
      permissions: route.permissions ?? [`${route.businessDomain}:read`],
    }));
}
